import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  SIZE,
  BLACK,
  WHITE,
  KOMI,
  createEmptyBoard,
  playMove,
  computeKoPoint,
  scoreBoard,
  getBestMove,
} from './utils/goAI';

const AI_THINK_DELAY = 500;
const STAR_POINTS = new Set([2, 6, 4].flatMap((x) => [2, 6, 4].map((y) => `${x}-${y}`)));

export default function CoVayPage() {
  const [board, setBoard] = useState(createEmptyBoard);
  const [toMove, setToMove] = useState(BLACK);
  const [koPoint, setKoPoint] = useState(null);
  const [passStreak, setPassStreak] = useState(0);
  const [capturesBy, setCapturesBy] = useState({ [BLACK]: 0, [WHITE]: 0 });
  const [lastMove, setLastMove] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [finalScore, setFinalScore] = useState(null);
  const [aiThinking, setAiThinking] = useState(false);
  const [stats, setStats] = useState({ [BLACK]: 0, [WHITE]: 0 });
  const thinkTimer = useRef(null);

  useEffect(() => () => clearTimeout(thinkTimer.current), []);

  const finishGame = (currentBoard) => {
    const score = scoreBoard(currentBoard);
    setFinalScore(score);
    setGameOver(true);
    setStats((s) => ({ ...s, [score.winner]: s[score.winner] + 1 }));
  };

  const runAiMove = (currentBoard, currentKo, currentPassStreak) => {
    setAiThinking(true);
    thinkTimer.current = setTimeout(() => {
      const move = getBestMove(currentBoard, WHITE, BLACK, {
        koPoint: currentKo,
        opponentJustPassed: currentPassStreak >= 1,
      });

      if (move.pass) {
        setAiThinking(false);
        const newStreak = currentPassStreak + 1;
        setPassStreak(newStreak);
        if (newStreak >= 2) {
          finishGame(currentBoard);
        } else {
          setToMove(BLACK);
        }
        return;
      }

      const result = playMove(currentBoard, move.x, move.y, WHITE);
      const newKo = computeKoPoint(result.board, move.x, move.y, result.captured);
      setBoard(result.board);
      setLastMove({ x: move.x, y: move.y });
      setCapturesBy((c) => ({ ...c, [WHITE]: c[WHITE] + result.captured.length }));
      setKoPoint(newKo);
      setPassStreak(0);
      setAiThinking(false);
      setToMove(BLACK);
    }, AI_THINK_DELAY);
  };

  const handleCellClick = (x, y) => {
    if (gameOver || aiThinking || toMove !== BLACK || board[y][x]) return;
    if (koPoint && koPoint.x === x && koPoint.y === y) return;

    const result = playMove(board, x, y, BLACK);
    if (!result.legal) return; // nước tự sát, không cho đánh

    const newKo = computeKoPoint(result.board, x, y, result.captured);
    setBoard(result.board);
    setLastMove({ x, y });
    setCapturesBy((c) => ({ ...c, [BLACK]: c[BLACK] + result.captured.length }));
    setKoPoint(newKo);
    setPassStreak(0);
    setToMove(WHITE);
    runAiMove(result.board, newKo, 0);
  };

  const handlePass = () => {
    if (gameOver || aiThinking || toMove !== BLACK) return;
    const newStreak = passStreak + 1;
    setPassStreak(newStreak);
    setKoPoint(null);
    setToMove(WHITE);
    if (newStreak >= 2) {
      finishGame(board);
      return;
    }
    runAiMove(board, null, newStreak);
  };

  const startNewRound = () => {
    clearTimeout(thinkTimer.current);
    setBoard(createEmptyBoard());
    setToMove(BLACK);
    setKoPoint(null);
    setPassStreak(0);
    setCapturesBy({ [BLACK]: 0, [WHITE]: 0 });
    setLastMove(null);
    setGameOver(false);
    setFinalScore(null);
    setAiThinking(false);
  };

  const isKoBlocked = (x, y) => !!koPoint && koPoint.x === x && koPoint.y === y;
  const isLastMove = (x, y) => !!lastMove && lastMove.x === x && lastMove.y === y;

  return (
    <div>
      <Helmet>
        <title>Cờ vây - XSMB</title>
        <meta name="description" content="Chơi cờ vây (Go/Weiqi) bàn 9x9 đấu với máy, tính điểm theo diện tích." />
      </Helmet>

      <div className="flex flex-col items-center">
        <div className="w-full max-w-md flex items-center justify-between mb-3">
          <div className="text-sm">
            <span className="font-semibold text-gray-800">⚫ Bạn (Đen)</span>
            <span className="mx-2 text-gray-300">·</span>
            <span className="font-semibold text-gray-500">⚪ Máy (Trắng, +{KOMI} komi)</span>
          </div>
        </div>

        <div className="w-full max-w-md grid grid-cols-2 gap-2 mb-2 text-center">
          <div className="rounded-lg border border-gray-200 bg-white py-2">
            <div className="text-lg font-extrabold text-gray-800">{stats[BLACK]}</div>
            <div className="text-[11px] text-gray-400">Bạn thắng</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white py-2">
            <div className="text-lg font-extrabold text-gray-500">{stats[WHITE]}</div>
            <div className="text-[11px] text-gray-400">Máy thắng</div>
          </div>
        </div>

        <div className="w-full max-w-md mb-3 text-[11px] text-gray-400 text-center">
          Quân bắt được — Bạn: <span className="font-semibold text-gray-600">{capturesBy[BLACK]}</span>
          {' · '}Máy: <span className="font-semibold text-gray-600">{capturesBy[WHITE]}</span>
        </div>

        <div className="w-full max-w-md mb-3 h-10 flex items-center justify-center">
          {gameOver ? (
            <div
              className={`w-full text-center text-sm font-bold rounded-lg border py-1 ${
                finalScore.winner === BLACK
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}
            >
              {finalScore.winner === BLACK ? '🎉 Bạn thắng' : '🤖 Máy thắng'} cách biệt {finalScore.diff} điểm
              <div className="text-[11px] font-normal mt-0.5">
                Đen {finalScore.blackScore} ({finalScore.blackStones} quân + {finalScore.blackTerritory} đất) — Trắng{' '}
                {finalScore.whiteScore} ({finalScore.whiteStones} quân + {finalScore.whiteTerritory} đất + {KOMI} komi)
              </div>
            </div>
          ) : aiThinking ? (
            <div className="text-xs text-gray-400 animate-pulse">🤖 Máy đang suy nghĩ...</div>
          ) : (
            <div className="text-xs text-gray-400 text-center">
              {toMove === BLACK ? 'Lượt của bạn' : 'Lượt của máy'}
              {passStreak === 1 && (
                <div className="text-[11px] text-amber-600 mt-0.5">
                  {toMove === BLACK ? 'Máy vừa bỏ lượt' : 'Bạn vừa bỏ lượt'} — bỏ lượt lần nữa để kết thúc ván
                </div>
              )}
            </div>
          )}
        </div>

        <div
          className="w-full max-w-md aspect-square grid border border-amber-800 bg-amber-100 select-none"
          style={{ gridTemplateColumns: `repeat(${SIZE}, minmax(0, 1fr))` }}
        >
          {board.map((row, y) =>
            row.map((cell, x) => {
              const koBlocked = isKoBlocked(x, y);
              const marked = isLastMove(x, y);
              return (
                <button
                  key={`${x}-${y}`}
                  onClick={() => handleCellClick(x, y)}
                  disabled={gameOver || aiThinking || toMove !== BLACK || !!cell || koBlocked}
                  title={koBlocked ? 'Luật Ko: chưa được đánh lại ngay ở đây' : undefined}
                  className={`relative aspect-square flex items-center justify-center border border-amber-800/30 ${
                    koBlocked ? 'bg-red-100' : ''
                  }`}
                >
                  {!cell && STAR_POINTS.has(`${x}-${y}`) && (
                    <span className="block rounded-full bg-amber-800/60" style={{ width: '18%', height: '18%' }} />
                  )}
                  {cell && (
                    <span
                      className={`block rounded-full shadow ${
                        cell === BLACK ? 'bg-gray-900' : 'bg-white border border-gray-400'
                      } ${marked ? 'ring-2 ring-offset-1 ring-red-500' : ''}`}
                      style={{ width: '82%', height: '82%' }}
                    />
                  )}
                </button>
              );
            })
          )}
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={handlePass}
            disabled={gameOver || aiThinking || toMove !== BLACK}
            className="bg-white hover:bg-gray-50 disabled:opacity-50 text-gray-700 border border-gray-300 px-6 py-3 rounded-xl font-bold text-sm transition-colors shadow"
          >
            🙌 Bỏ lượt
          </button>
          <button
            onClick={startNewRound}
            disabled={aiThinking}
            className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white px-8 py-3 rounded-xl font-bold text-sm transition-colors shadow-lg"
          >
            {gameOver ? '🔄 Ván mới' : '🔄 Chơi lại'}
          </button>
        </div>

        <div className="mt-3 text-[11px] text-gray-400 text-center max-w-md">
          Bàn 9x9, tính điểm theo diện tích (quân + đất bao quanh). Bạn luôn cầm quân Đen đi trước, máy cầm Trắng
          được cộng {KOMI} điểm komi. Bỏ lượt 2 lần liên tiếp sẽ kết thúc ván. Kết quả chỉ lưu trong phiên chơi này,
          rời trang là mất.
        </div>
      </div>
    </div>
  );
}
